import React, { useCallback, useEffect, useState } from 'react';
import { toastr } from 'react-redux-toastr';

import { fetchWhatsappMessageMedia } from '../../../services/operational/whatsappChats';
import ChatMediaViewerModal from './ChatMediaViewerModal';
import {
  ChatMediaAudio,
  ChatMediaBlock,
  ChatMediaDocument,
  ChatMediaDocumentIcon,
  ChatMediaDocumentLabel,
  ChatMediaHint,
  ChatMediaImage,
  ChatMediaImageButton,
} from './chatsWhatsapp.styles';

const documentIcon = (mime = '') => {
  if (mime.startsWith('image/')) return '🖼️';
  if (mime === 'application/pdf' || mime.includes('pdf')) return '📄';
  if (mime.startsWith('audio/')) return '🎵';
  return '📎';
};

const isPdfMime = (mime = '') => {
  const normalized = String(mime).toLowerCase();
  return normalized === 'application/pdf' || normalized.includes('pdf');
};

const downloadBlob = (blob, filename, mime) => {
  const url = URL.createObjectURL(new Blob([blob], { type: mime || blob.type }));
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filename || 'arquivo';
  anchor.click();
  setTimeout(() => URL.revokeObjectURL(url), 60000);
};

const ChatMessageContent = ({ msg, token, conversationUuid }) => {
  const media = msg.media;
  const bodyText = (msg.body_text || '').trim();
  const hasText = bodyText !== '';
  const [imageUrl, setImageUrl] = useState(null);
  const [audioUrl, setAudioUrl] = useState(null);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [opening, setOpening] = useState(false);
  const [viewer, setViewer] = useState(null);

  useEffect(() => {
    if (!media || !token || !conversationUuid) {
      setImageUrl(null);
      setAudioUrl(null);
      return undefined;
    }

    if (media.kind !== 'image' && media.kind !== 'audio') {
      setImageUrl(null);
      setAudioUrl(null);
      return undefined;
    }

    let cancelled = false;
    let objectUrl = null;
    setLoadingPreview(true);

    fetchWhatsappMessageMedia(token, conversationUuid, msg.id)
      .then((res) => {
        if (cancelled) return;
        if (res.status >= 400) {
          return;
        }
        objectUrl = URL.createObjectURL(res.data);
        if (media.kind === 'image') {
          setImageUrl(objectUrl);
        } else {
          setAudioUrl(objectUrl);
        }
      })
      .finally(() => {
        if (!cancelled) {
          setLoadingPreview(false);
        }
      });

    return () => {
      cancelled = true;
      if (objectUrl) {
        URL.revokeObjectURL(objectUrl);
      }
    };
  }, [conversationUuid, media, msg.id, token]);

  const openViewer = useCallback((nextViewer) => {
    setViewer((current) => {
      if (current?.revoke && current.url) {
        URL.revokeObjectURL(current.url);
      }
      return nextViewer;
    });
  }, []);

  const closeViewer = useCallback(() => {
    openViewer(null);
  }, [openViewer]);

  const openStoredMedia = useCallback(async () => {
    if (!token || !conversationUuid) return;
    setOpening(true);
    try {
      const res = await fetchWhatsappMessageMedia(token, conversationUuid, msg.id);
      if (res.status >= 400) {
        toastr.error('Chats', 'Não foi possível abrir o arquivo.');
        return;
      }

      const mime = media?.mime || res.data.type || 'application/octet-stream';
      const filename = media?.filename || 'arquivo';

      if (media?.kind === 'image') {
        const url = URL.createObjectURL(new Blob([res.data], { type: mime }));
        openViewer({
          mode: 'image',
          url,
          filename,
          mime,
          revoke: true,
        });
        return;
      }

      if (isPdfMime(mime)) {
        const url = URL.createObjectURL(new Blob([res.data], { type: mime }));
        openViewer({
          mode: 'pdf',
          url,
          filename,
          mime,
          revoke: true,
        });
        return;
      }

      downloadBlob(res.data, filename, mime);
    } catch (e) {
      toastr.error('Chats', 'Erro ao abrir o arquivo.');
    } finally {
      setOpening(false);
    }
  }, [conversationUuid, media, msg.id, openViewer, token]);

  const onOpenMedia = useCallback(async () => {
    if (media?.kind === 'image' && imageUrl) {
      openViewer({
        mode: 'image',
        url: imageUrl,
        filename: media.filename || 'imagem',
        mime: media.mime,
        revoke: false,
      });
      return;
    }

    await openStoredMedia();
  }, [imageUrl, media, openStoredMedia, openViewer]);

  const handleViewerDownload = useCallback(() => {
    if (!viewer?.url) return;
    const anchor = document.createElement('a');
    anchor.href = viewer.url;
    anchor.download = viewer.filename || 'arquivo';
    anchor.click();
  }, [viewer]);

  const handleViewerOpenExternal = useCallback(() => {
    if (!viewer?.url) return;
    window.open(viewer.url, '_blank', 'noopener,noreferrer');
  }, [viewer]);

  const renderMedia = () => {
    if (!media) {
      if (!hasText) {
        return `[${msg.content_type || 'mensagem'}]`;
      }
      return null;
    }

    if (media.kind === 'image') {
      return (
        <ChatMediaBlock $hasText={hasText}>
          {loadingPreview && <ChatMediaHint>Carregando imagem…</ChatMediaHint>}
          {!loadingPreview && imageUrl && (
            <ChatMediaImageButton type="button" onClick={onOpenMedia} disabled={opening}>
              <ChatMediaImage src={imageUrl} alt={media.filename || 'Imagem enviada'} />
            </ChatMediaImageButton>
          )}
          {!loadingPreview && !imageUrl && (
            <ChatMediaDocument type="button" onClick={onOpenMedia} disabled={opening} $hasText={false}>
              <ChatMediaDocumentIcon>{documentIcon(media.mime)}</ChatMediaDocumentIcon>
              <ChatMediaDocumentLabel>{media.filename || 'Ver imagem'}</ChatMediaDocumentLabel>
            </ChatMediaDocument>
          )}
        </ChatMediaBlock>
      );
    }

    if (media.kind === 'audio') {
      return (
        <ChatMediaBlock $hasText={hasText}>
          {audioUrl ? (
            <ChatMediaAudio $hasText={hasText} controls src={audioUrl}>
              Seu navegador não suporta áudio.
            </ChatMediaAudio>
          ) : (
            <ChatMediaDocument type="button" onClick={onOpenMedia} disabled={opening || loadingPreview} $hasText={false}>
              <ChatMediaDocumentIcon>🎵</ChatMediaDocumentIcon>
              <ChatMediaDocumentLabel>{media.filename || 'Ouvir áudio'}</ChatMediaDocumentLabel>
            </ChatMediaDocument>
          )}
        </ChatMediaBlock>
      );
    }

    const label =
      media.filename ||
      (media.source === 'laudo' ? `Laudo da solicitação #${media.solicitacao_id}` : 'Abrir arquivo');

    return (
      <ChatMediaDocument type="button" onClick={onOpenMedia} disabled={opening} $hasText={hasText}>
        <ChatMediaDocumentIcon>{documentIcon(media.mime)}</ChatMediaDocumentIcon>
        <ChatMediaDocumentLabel>{opening ? 'Abrindo…' : label}</ChatMediaDocumentLabel>
      </ChatMediaDocument>
    );
  };

  return (
    <>
      {hasText ? bodyText : null}
      {renderMedia()}
      <ChatMediaViewerModal
        viewer={viewer}
        onClose={closeViewer}
        onDownload={viewer && !viewer.external ? handleViewerDownload : undefined}
        onOpenExternal={viewer?.external ? handleViewerOpenExternal : undefined}
      />
    </>
  );
};

export default ChatMessageContent;
